import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useDeleteMfaPreference,
  usePostMfaPreference,
} from 'src/api/mutation/mfaPreference'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { MfaPreferences } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { manageTrustedDevicesUrl } from 'src/navigation/urls'
import { createSetIsChoose2faMethodPopupOpen } from 'src/redux/actions/app.actions'
import { selectCognitoUserFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface TwoFactorAuthenticationProfileBoxProps {
  hideOnOff?: boolean
}

const TwoFactorAuthenticationProfileBox: React.FC<
  TwoFactorAuthenticationProfileBoxProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const user = useCurrentUser()
  const cognitoUser = useSelector(selectCognitoUserFromState)
  const mfaOn = user?.mfaPreference === MfaPreferences.email

  const dispatch = useDispatch()
  const postMfaPreference = usePostMfaPreference()
  const deleteMfaPreference = useDeleteMfaPreference()

  const dispatchTurnOnOrOffMfa = (onOff: boolean): void => {
    if (cognitoUser) {
      if (onOff) {
        postMfaPreference.mutate({ userName: cognitoUser.username })
      } else {
        deleteMfaPreference.mutate()
      }
    }
  }

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        padding: 40px;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}
      >
        {translate('Two-Factor Authentication')}
        {!props.hideOnOff && (
          <OnOffSwitch
            defaultOnOffState={mfaOn}
            onChange={dispatchTurnOnOrOffMfa}
          />
        )}
      </span>
      <div
        css={css`
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
          margin-top: 16px;
          margin-bottom: 40px;
        `}
      >
        {translate(
          'Add an extra layer of security to your accounts by using a one-time security code in addition to your password each time you log in. When you sign in on a new device or web browser, a verification code will be sent to the email associated with your account.',
        )}
      </div>
      <div
        css={css`
          width: fit-content;
          display: flex;
          flex-direction: column;
          gap: 28px;
        `}
      >
        <HoverableLink
          ariaLabelSource={'Choose default 2FA method'}
          onClick={(): void => {
            dispatch(createSetIsChoose2faMethodPopupOpen(true))
          }}
        >
          <span
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
            `}
          >
            {translate('Choose default 2FA method')}
            <SvgArrowForward
              css={css`
                margin-left: 4px;
              `}
              style={{
                transform: 'scale(0.667)',
                transformOrigin: 'left',
                flexShrink: 0,
              }}
            />
          </span>
        </HoverableLink>
        <HoverableLink
          ariaLabelSource={'Manage trusted devices'}
          to={manageTrustedDevicesUrl()}
        >
          <span
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
            `}
          >
            {translate('Manage trusted devices')}
            <SvgArrowForward
              css={css`
                margin-left: 4px;
              `}
              style={{
                transform: 'scale(0.667)',
                transformOrigin: 'left',
                flexShrink: 0,
              }}
            />
          </span>
        </HoverableLink>
      </div>
    </Box>
  )
}

export default TwoFactorAuthenticationProfileBox
